import { Component, OnInit, inject, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { EmpresaCgr } from 'src/app/domain/model/empresa/empresa-cgr';
import { EmpresaCgrRepository } from 'src/app/domain/service/empresa/empresa-cgr-repository';
import { HttpEmpresaCgrRepository } from 'src/app/infrastructure/adapters/empresa/http-empresa-cgr.repository';
import { CatalogoRepository } from 'src/app/domain/service/catalogo/catalogo-repository';
import { OffsetPageResponse } from 'src/app/domain/model/common/offset-page-response.dto';
import { DEFAULT_ROWS_PER_PAGE, PAGE_SIZE_OPTIONS } from 'src/app/common/constants-ejb';
import { BusquedaPaginadorComponent } from 'src/app/presentation/shared/busquedas/busqueda-paginador.component';
import { sanitizeText } from 'src/app/common/input-utils';

/**
 * Buscador de Empresas CGR via SP_DIS_BUSCAR_EMPRESAS.
 *
 * DIFERENCIA con BusquedaClienteDialogComponent:
 * - Este componente usa SP_DIS_BUSCAR_EMPRESAS (tablas distribuidor CGR).
 * - BusquedaClienteDialogComponent usa TPSC_BENEFICIARIO + TPSC_PERSONAS.
 * - Usar este buscador para el filtro "Empresa" de Aprobar/Rechazar Informes (ivadniempresa).
 *
 * Legacy: BuscadorEmpresaViewImpl + BuscarEmpresaViewImpl.
 */
@Component({
  selector: 'app-busqueda-empresa-cgr-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    DropdownModule,
    InputTextModule,
    TableModule,
    TooltipModule,
    BusquedaPaginadorComponent
  ],
  styleUrls: ['../../shared/styles/grid-table.styles.scss'],
  providers: [
    { provide: EmpresaCgrRepository, useClass: HttpEmpresaCgrRepository }
  ],
  template: `
    <p-dialog
      header="Busqueda de Empresas"
      [(visible)]="visible"
      [modal]="true"
      [style]="{ width: '92vw', maxWidth: '54rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '1024px': '88vw', '768px': '92vw', '640px': '96vw' }"
      [closable]="true"
      (onHide)="onCancelar()"
    >
      <!-- Filtros — mismo estilo que BusquedaProfesionalDialogComponent -->
      <div class="border dark:border-2 rounded p-4 mb-4 bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
        <div class="font-medium mb-3">Filtros de Busqueda</div>
        <div class="grid grid-cols-1 gap-3">
          <div>
            <label class="block text-sm text-gray-600 mb-1">Identificacion</label>
            <div class="flex gap-2">
              <div class="flex-1">
              <p-dropdown
                [(ngModel)]="filtroTipo"
                [options]="tiposIdentificacion()"
                optionLabel="descripcion"
                optionValue="codigo"
                [filter]="true"
                filterBy="descripcion"
                placeholder="Tipo"
                [showClear]="true"
                appendTo="body"
                scrollHeight="150px"
                styleClass="w-full"
              />
              </div>
              <input pInputText [(ngModel)]="filtroNumero"
                (ngModelChange)="filtroNumero = sanitizeText($event)"
                (keyup.enter)="buscar()"
                maxlength="30" class="flex-1" placeholder="Numero" />
            </div>
          </div>
          <div>
            <label class="block text-sm text-gray-600 mb-1">Nombre</label>
            <input pInputText [(ngModel)]="filtroNombre"
              (ngModelChange)="filtroNombre = sanitizeText($event)"
              (keyup.enter)="buscar()"
              maxlength="200" class="w-full" placeholder="Nombre de la empresa" />
          </div>
        </div>
        <div class="flex flex-wrap gap-2 mt-4">
          <button pButton icon="pi pi-trash" label="Limpiar"
            pTooltip="Limpiar los parametros ingresados"
            class="p-button-sm p-button-secondary"
            (click)="limpiar()"></button>
          <button pButton icon="pi pi-search" label="Buscar"
            pTooltip="Buscar con los parametros ingresados"
            class="p-button-sm"
            (click)="buscar()" [disabled]="cargando()"></button>
        </div>
      </div>

      @if (iniciado()) {
        <div class="border rounded">
          <div class="font-medium text-gray-700 p-3 border-b bg-gray-50 text-sm flex justify-between items-center">
            <span>Listado de Empresas</span>
            @if (totalRegistros() > 0) {
              <span class="text-xs text-gray-500">
                Registros del {{ registroDesde() }} hasta el {{ registroHasta() }} de {{ totalRegistros() }}
              </span>
            }
          </div>

          <!-- Vista mobile -->
          <div class="md:hidden space-y-3 p-3">
            @for (empresa of empresas(); track $index) {
              <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900">
                <div class="grid grid-cols-1 gap-1 text-sm mb-3">
                  <div>
                    <span class="text-gray-500">Identificacion:</span>
                    <span class="text-gray-800 dark:text-gray-200 ml-1">{{ textoTabla(empresa.numeroDocumento) }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500">Nombre:</span>
                    <span class="text-gray-800 dark:text-gray-200 ml-1">{{ textoTabla(empresa.nombre) }}</span>
                  </div>
                </div>
                <p-button label="Seleccionar" icon="pi pi-check" size="small"
                  styleClass="w-full min-h-[44px]" (onClick)="seleccionar(empresa)" />
              </div>
            }
            @if (empresas().length === 0) {
              <div class="text-center py-4 text-gray-500">No se encontraron empresas.</div>
            }
          </div>

          <!-- Vista desktop: loading en tabla, Seleccionar por boton -->
          <div class="hidden md:block">
            <p-table
              [value]="empresas()"
              styleClass="tabla-busqueda-empresa-cgr p-datatable-sm p-datatable-gridlines"
              [scrollable]="true"
              [responsiveLayout]="'scroll'"
              [scrollHeight]="'300px'"
              [tableStyle]="{ 'min-width': 'max-content' }"
              [paginator]="false"
              [loading]="cargando()"
            >
              <ng-template pTemplate="header">
                <tr>
                  <th pResizableColumn><span class="grid-header-text" title="Identificacion">Identificacion</span></th>
                  <th pResizableColumn><span class="grid-header-text" title="Nombre">Nombre</span></th>
                  <th pResizableColumn><span class="grid-header-text" title="Seleccionar">Seleccionar</span></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-empresa>
                <tr>
                  <td><span class="grid-cell-text" [attr.title]="tituloTabla(empresa.numeroDocumento)">{{ textoTabla(empresa.numeroDocumento) }}</span></td>
                  <td><span class="grid-cell-text" [attr.title]="tituloTabla(empresa.nombre)">{{ textoTabla(empresa.nombre) }}</span></td>
                  <td>
                    <button pButton label="Seleccionar" class="p-button-sm p-button-text"
                      (click)="seleccionar(empresa)"></button>
                  </td>
                </tr>
              </ng-template>
              <ng-template pTemplate="emptymessage">
                <tr>
                  <td colspan="3" class="text-center text-gray-500 p-4">No se encontraron empresas.</td>
                </tr>
              </ng-template>
            </p-table>
          </div>

          @if (totalRegistros() > 0) {
            <app-busqueda-paginador
              [currentPage]="currentPage()"
              [totalPages]="totalPaginas()"
              [hasPrevious]="pageResponse().hasPrevious"
              [hasNext]="pageResponse().hasNext"
              [pageSizeOptions]="pageSizeOptionsRaw"
              [selectedPageSize]="pageSize()"
              (pageSizeChange)="onPageSizeChange($event)"
              (previous)="irAPagina(currentPage() - 1)"
              (next)="irAPagina(currentPage() + 1)"
              (first)="irAPagina(1)"
              (last)="irAPagina(totalPaginas())"
            />
          }
        </div>
      }

      <!-- Footer con Cancelar — igual que BusquedaProfesionalDialogComponent -->
      <ng-template pTemplate="footer">
        <div class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full">
          <p-button
            label="Cancelar"
            icon="pi pi-times"
            [outlined]="true"
            (onClick)="onCancelar()"
          ></p-button>
        </div>
      </ng-template>
    </p-dialog>
  `
})
export class BusquedaEmpresaCgrDialogComponent implements OnInit {

  readonly sanitizeText = sanitizeText;

  onSelect = output<EmpresaCgr>();
  onClose  = output<void>();

  private readonly repo        = inject(EmpresaCgrRepository);
  private readonly catalogoRepo = inject(CatalogoRepository);

  filtroTipo   = '';
  filtroNumero = '';
  filtroNombre = '';
  tiposIdentificacion = signal<{ codigo: string; descripcion: string }[]>([]);

  iniciado = signal(false);
  cargando = signal(false);
  visible  = true;

  private _pageResponse = signal<OffsetPageResponse<EmpresaCgr>>({
    content: [], totalElements: 0, pageNumber: 0, pageSize: DEFAULT_ROWS_PER_PAGE,
    totalPages: 0, first: true, last: true, hasNext: false, hasPrevious: false
  });

  readonly pageResponse = this._pageResponse.asReadonly();
  empresas       = () => this._pageResponse().content;
  totalRegistros = () => this._pageResponse().totalElements;
  currentPage    = () => this._pageResponse().pageNumber + 1;
  totalPaginas   = () => this._pageResponse().totalPages;
  pageSize       = () => this._pageResponse().pageSize;
  registroDesde  = () => this.totalRegistros() === 0
    ? 0
    : this._pageResponse().pageNumber * this._pageResponse().pageSize + 1;
  registroHasta  = () =>
    Math.min(this.registroDesde() + this.empresas().length - 1, this.totalRegistros());

  pageSizeOptionsRaw = PAGE_SIZE_OPTIONS;

  async ngOnInit(): Promise<void> {
    try {
      const tipos = await this.catalogoRepo.getTiposIdentificacion();
      this.tiposIdentificacion.set(tipos);
    } catch {
      // no critico: el filtro de tipo quedara vacio
    }
  }

  async buscar(): Promise<void> {
    this.iniciado.set(true);
    await this.cargarPagina(0);
  }

  limpiar(): void {
    this.filtroTipo   = '';
    this.filtroNumero = '';
    this.filtroNombre = '';
    this.iniciado.set(false);
    this._pageResponse.set({
      content: [], totalElements: 0, pageNumber: 0, pageSize: DEFAULT_ROWS_PER_PAGE,
      totalPages: 0, first: true, last: true, hasNext: false, hasPrevious: false
    });
  }

  irAPagina(page: number): void { this.cargarPagina(page - 1); }

  onPageSizeChange(size: number): void {
    this._pageResponse.update(r => ({ ...r, pageSize: size }));
    this.cargarPagina(0);
  }

  private async cargarPagina(pageNumber: number): Promise<void> {
    this.cargando.set(true);
    try {
      const dniFilter = this.filtroTipo
        ? this.filtroTipo + (this.filtroNumero?.trim() ?? '')
        : (this.filtroNumero?.trim() || null);

      const result = await this.repo.buscar(
        dniFilter,
        this.filtroNombre?.trim() || null,
        pageNumber,
        this.pageSize()
      );
      this._pageResponse.set(result);
    } catch (err) {
      console.error('Error buscando empresas CGR', err);
    } finally {
      this.cargando.set(false);
    }
  }

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  tituloTabla(valor: unknown): string {
    return String(valor ?? '').trim();
  }

  seleccionar(empresa: EmpresaCgr): void {
    this.onSelect.emit(empresa);
  }

  onCancelar(): void {
    this.visible = false;
    this.onClose.emit();
  }
}